<script setup lang="ts">
import { computed } from 'vue'
import { onBeforeRouteLeave, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import { useStaffStore } from '../stores/staffStore'
import type { IStaffEmployee, TEmployeeStatus } from '../types/IStaffEmployee'

// Диалоги
import StructureModal from './StructureModal.vue'

// import BlockEmployeeDialog from './BlockEmployeeDialog.vue'
// import DismissEmployeeDialog from './DismissEmployeeDialog.vue'
// import RestoreEmployeeDialog from './RestoreEmployeeDialog.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import ReportTemplateMenu from '@/components/createdUi/ReportTemplateMenu.vue'
import { useExportUrl } from '@/composables/useExportUrl'
import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { usePermissionsStore } from '@/modules/permissions/stores/permissionsStore'
import { staffReportFilters } from '@/modules/staff/utils/reportFilters'
import { ERouteNames } from '@/router/ERouteNames'
import { formatRelativeTime, getFullName } from '@/utils/formatters'
import { getRandomAvatar } from '@/utils/getRandomAvatar'
import { useTableHideCompleted } from '@/composables/useTableHideCompleted'
import { openOrCreateChat } from '@/utils/openChat'
import AddEmployeeDialog from './AddEmployeeDialog.vue'
import DepartmentInfoSidebar from './DepartmentInfoSidebar.vue'
import EditEmployeeDialog from './EditEmployeeDialog.vue'
import RoleSettingsDialog from './RoleSettingsDialog.vue'
import OnboardingLaunchButton from '@/modules/onboarding/components/OnboardingLaunchButton.vue'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoRows } from '@/modules/onboarding/composables/useOnboardingDemoRows'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { STAFF_ANCHORS, STAFF_CONTEXTS } from '@/modules/onboarding/scenarios/staff/anchors'
import { STAFF_ACTIVITY_DEMO_INDEX } from '@/modules/onboarding/scenarios/staff/adapter'
import { ONBOARDING_DEMO_QUERY } from '@/modules/onboarding/utils/demoRoute'

const { smAndDown } = useDisplay()

const staffStore = useStaffStore()
const permissionsStore = usePermissionsStore()
const apiStore = useApiStore()
const feedbackStore = useFeedbackStore()

// Синхронно сбрасываем список и ставим loading в true при инициализации
staffStore.isLoading = true
staffStore.staffList = []

const { itemsPerPage } = useTableItemsPerPage('staff', 25)
const { hideCompleted } = useTableHideCompleted('staff')
staffStore.filters.is_actual = hideCompleted.value ? true : null
const selectedEmployees = ref<string[]>([])
const reportFilters = computed(() => staffReportFilters(staffStore.filters))
const selectedEmployeesCache = ref<Map<string, IStaffEmployee>>(new Map())

const isRoleSettingsDialogOpen = ref(false)
const roleSettingsEmployeeIds = ref<string[]>([])

const isEditEmployeeDialogOpen = ref(false)
const editEmployeeId = ref<string | null>(null)

function openEditEmployeeDialog(item: IStaffEmployee) {
  editEmployeeId.value = item.id
  isEditEmployeeDialogOpen.value = true
}

function openBulkRoleSettings() {
  roleSettingsEmployeeIds.value = [...selectedEmployees.value]
  isRoleSettingsDialogOpen.value = true
}

function openSingleRoleSettings(item: IStaffEmployee) {
  roleSettingsEmployeeIds.value = [item.id]
  selectedEmployeesCache.value.set(item.id, item)
  isRoleSettingsDialogOpen.value = true
}

function onRoleSettingsSaved() {
  selectedEmployees.value = selectedEmployees.value.filter(id => !roleSettingsEmployeeIds.value.includes(id))
  roleSettingsEmployeeIds.value.forEach(id => selectedEmployeesCache.value.delete(id))
}

watch(roleSettingsEmployeeIds, newIds => {
  if (isRoleSettingsDialogOpen.value && selectedEmployees.value.length > 0) {
    selectedEmployees.value = selectedEmployees.value.filter(id => newIds.includes(id))
    for (const id of selectedEmployeesCache.value.keys()) {
      if (!newIds.includes(id)) {
        selectedEmployeesCache.value.delete(id)
      }
    }
  }
})

// Конфигурация фильтров
const filterConfigs: IFilterConfig[] = [
  {
    key: 'departments',
    labelPrefix: 'Подразделение: ',
    type: 'tree',
  },
  {
    key: 'positions',
    labelPrefix: 'Должность: ',
    type: 'tree',
  },
  {
    key: 'roles',
    labelPrefix: 'Роль: ',
    type: 'tree',
  },
  {
    key: 'status',
    labelPrefix: 'Статус: ',
    type: 'tree',
    getCustomLabel: (value: unknown) => getStatusLabel(value as TEmployeeStatus),
  },
  {
    key: 'last_login',
    labelPrefix: 'Последний вход: ',
    type: 'date_range',
    rangeStartKey: 'last_login_after',
    rangeEndKey: 'last_login_before',
  },
  {
    key: 'is_actual',
    labelPrefix: '',
    type: 'boolean',
    silent: true,
  },
]

// Инициализация composable useTableFilters
const tableLogic = useTableFilters({
  filters: computed(() => staffStore.filters),
  filterChoices: computed(() => staffStore.filterChoices) as unknown as Record<string, unknown[]>,
  searchChoices: computed(() => staffStore.searchChoices) as unknown as Record<string, unknown[]>,
  fetchFilterChoices: fieldKey => staffStore.fetchFilterChoices(fieldKey),
  filterLoading: staffStore.filterLoading,
  fetchData: (cursor, pageSize) => staffStore.fetchStaff(cursor, pageSize),
  currentSortString: toRef(staffStore, 'currentSortString'),
  itemsPerPage,
  resetFilters: () => staffStore.resetFilters(),
  nextCursor: toRef(staffStore, 'nextCursor'),
  loadedItemsCount: computed(() => staffStore.staffList.length),
  totalItemsCount: computed(() => staffStore.totalItemsCount),
  sortMap: {
    name: 'full_name',
    role: 'roles',
    position: 'position_name',
    last_login: 'last_login',
    department: 'department_name',
    status: 'status',
  },
  filterConfigs,
  resetOnUnmount: false,
})

const isHideInactive = computed({
  get: () => staffStore.filters.is_actual === true,
  set: (val: boolean) => {
    staffStore.filters.is_actual = val ? true : null
    hideCompleted.value = val
    tableLogic.triggerServerFilter()
  },
})

onBeforeRouteLeave(to => {
  if (to.name !== ERouteNames.STAFF_ACTIVITY) {
    staffStore.resetFilters()
  }
})

function parseDateFromInput(dateString: string): Date | null {
  if (!dateString) {
    return null
  }
  const [day, month, year] = dateString.split('.').map(Number)
  if (!day || !month || !year) {
    return null
  }

  return new Date(year, month - 1, day)
}

function applyLastLoginDateFilter() {
  staffStore.filters.last_login_after = parseDateFromInput(tableLogic.tempValues.value.last_login_after || '')
  staffStore.filters.last_login_before = parseDateFromInput(tableLogic.tempValues.value.last_login_before || '')
  tableLogic.filterMenus.value.last_login = false
  tableLogic.triggerServerFilter()
}

// Состояния диалогов
const isStructureModalOpen = ref(false)
const isBlockDialogOpen = ref(false)
const isBulkBlock = ref(false)
const isDismissDialogOpen = ref(false)
const isRestoreDialogOpen = ref(false)
const isBulkRestore = ref(false)
const isProfileSidebarOpen = ref(false)
const isDepartmentSidebarOpen = ref(false)
const isAddEmployeeDialogOpen = ref(false)
const selectedDepartmentId = ref('')
const selectedDepartmentName = ref('')
const selectedEmployeeForSidebar = ref<IStaffEmployee | null>(null)

const selectedEmployee = ref<IStaffEmployee | null>(null)
const selectedEmployeeId = ref<string>('')

const selectedEmployeesList = computed(() => {
  return selectedEmployees.value.map(id => {
    const emp = staffStore.staffList.find(e => e.id === id) || selectedEmployeesCache.value.get(id)
    return emp || { id, full_name: 'Неизвестный сотрудник', last_name: 'Неизвестный', first_name: 'Сотрудник', avatar: null, position: null }
  })
})

const employeeToBlockName = computed(() => {
  if (selectedEmployee.value) {
    return selectedEmployee.value.full_name || [selectedEmployee.value.last_name, selectedEmployee.value.first_name, selectedEmployee.value.middle_name].filter(Boolean).join(' ')
  }
  if (selectedEmployees.value.length === 1) {
    const id = selectedEmployees.value[0]
    const emp = selectedEmployeesCache.value.get(id) || staffStore.staffList.find(e => e.id === id)
    if (emp) {
      return emp.full_name || [emp.last_name, emp.first_name, emp.middle_name].filter(Boolean).join(' ')
    }
  }
  return ''
})

function getEmployeeAvatar(emp: IStaffEmployee | { avatar?: string | null } | null | undefined) {
  if (emp && emp.avatar) {
    return emp.avatar
  }
  return getRandomAvatar()
}

const selectedInactiveEmployees = computed(() => {
  return selectedEmployeesList.value.filter(emp => 'status' in emp && emp.status === 'Не активен')
})

function removeEmployeeFromSelection(id: string) {
  selectedEmployees.value = selectedEmployees.value.filter(empId => empId !== id)
  selectedEmployeesCache.value.delete(id)
  if (selectedEmployees.value.length === 0) {
    isBlockDialogOpen.value = false
  }
  if (selectedInactiveEmployees.value.length === 0) {
    isRestoreDialogOpen.value = false
  }
}

const router = useRouter()

function openDepartmentInfo(id?: string, name?: string, emp?: IStaffEmployee) {
  if (!id) return
  selectedDepartmentId.value = id
  selectedDepartmentName.value = name || ''
  selectedEmployeeForSidebar.value = emp || null
  isDepartmentSidebarOpen.value = true
}

function navigateToActivity(id: string) {
  if (!permissionsStore.modules.employee_management?.employee_view) return
  router.push({ name: ERouteNames.STAFF_ACTIVITY, params: { id } })
}

function onRowClick(_event: unknown, row: { item: IStaffEmployee }) {
  if (!permissionsStore.modules.employee_management?.employee_view || staffStore.isLoading || !row.item.id || row.item.id.startsWith('fake-')) return
  navigateToActivity(row.item.id)
}

// Загрузка сотрудников при инициализации
onMounted(async () => {
  staffStore.isLoading = true
  if (!apiStore.departmentsTree || !apiStore.departmentsTree.length) {
    await apiStore.fetchAllDepartments()
  }
  await tableLogic.triggerServerFilter()

  // Автостарт только на самой таблице сотрудников
  if (router.currentRoute.value.name === ERouteNames.STAFF)
    void onboardingStore.autoStart('staff')
})

function getDepartmentHierarchy(departmentId?: string | null): string {
  if (!departmentId) return ''

  const path: string[] = []

  function dfs(nodes: any[]): boolean {
    for (const node of nodes) {
      if (!node) continue
      path.push(node.label || '')
      if (String(node.id || node.key) === String(departmentId)) {
        return true
      }
      if (node.children && node.children.length && dfs(node.children)) {
        return true
      }
      path.pop()
    }
    return false
  }

  dfs(apiStore.departmentsTree)
  return path.join(' > ')
}

const headers = computed(() => [
  {
    title: (!permissionsStore.modules.employee_management?.employee_view && !permissionsStore.modules.employee_management?.employee_edit && !permissionsStore.modules.employee_management?.role_edit) ? 'Меню' : '',
    key: 'select_and_menu',
    sortable: false,
    width: '100px',
  },
  {
    title: 'ФИО',
    key: 'name',
    sortable: true,
    width: '25%',
  },
  ...(permissionsStore.modules.employee_management?.employee_view
    ? [{
        title: 'Роль',
        key: 'role',
        sortable: true,
        filterType: 'tree',
        filterKey: 'roles',
        width: '15%',
      }]
    : []),
  {
    title: 'Должность',
    key: 'position',
    sortable: true,
    filterType: 'tree',
    filterKey: 'positions',
    width: '18%',
  },
  {
    title: 'Телефон',
    key: 'phone',
    sortable: false,
    width: '15%',
  },
  ...(permissionsStore.modules.employee_management?.employee_view
    ? [{
        title: 'Последний вход',
        key: 'last_login',
        sortable: true,
        filterType: 'date_range',
        filterKey: 'last_login',
        width: '15%',
      }]
    : []),
  {
    title: 'Подразделение',
    key: 'department',
    sortable: true,
    filterType: 'tree',
    filterKey: 'departments',
  },
  ...(permissionsStore.modules.employee_management?.employee_view
    ? [{
        title: 'Статус',
        key: 'status',
        sortable: true,
        align: 'center' as const,
        filterType: 'tree',
        filterKey: 'status',
        width: '10%',
      }]
    : []),
])

// Фильтрация сотрудников по строке поиска (данные фильтруются на сервере)
const filteredStaff = computed(() => {
  if (staffStore.isLoading && staffStore.staffList.length === 0) {
    return Array.from({ length: itemsPerPage.value }, (_, i) => ({
      id: `fake-${i}`,
      full_name: '',
      roles: [],
      position: null,
      last_login: '',
      department: null,
      status: 'active',
    })) as unknown as IStaffEmployee[]
  }
  return staffStore.staffList
})

/* Онбординг: демо-сотрудники первыми в таблице и действия, которыми сценарий открывает «Структуру», настройки и активность */
const onboardingStore = useOnboardingStore()
const { rows: tableRows } = useOnboardingDemoRows('employee', filteredStaff)
const onboardingActivityDemoId = computed(() => onboardingStore.activeDemoItems[STAFF_ACTIVITY_DEMO_INDEX]?.list.id ?? null)

useOnboardingContext({
  names: [STAFF_CONTEXTS.table],
  scenarioId: 'staff',
  root: () => document.body,
  actions: {
    openStructure: () => { isStructureModalOpen.value = true },
    closeStructure: () => { isStructureModalOpen.value = false },
    openDemoEdit: () => {
      if (onboardingActivityDemoId.value === null)
        return
      editEmployeeId.value = String(onboardingActivityDemoId.value)
      isEditEmployeeDialogOpen.value = true
    },
    closeEdit: () => { isEditEmployeeDialogOpen.value = false },
    openSettings: async () => { await router.push({ name: ERouteNames.STAFF_SETTINGS }) },
    openDemoActivity: async () => {
      if (onboardingActivityDemoId.value !== null)
        await router.push({ name: ERouteNames.STAFF_ACTIVITY, params: { id: String(onboardingActivityDemoId.value) }, query: { [ONBOARDING_DEMO_QUERY]: '1' } })
    },
  },
})

const rowProps = (data: { item: IStaffEmployee }) => ({
  'data-onboarding': data.item.id === onboardingActivityDemoId.value ? STAFF_ANCHORS.table.demoRow : undefined,
})

// Хелперы выбора чекбоксов
const isAllSelected = computed(() => {
  const realItems = filteredStaff.value.filter(e => !e.id.startsWith('fake-'))
  return realItems.length > 0 && selectedEmployees.value.length === realItems.length
})

const isSomeSelected = computed(() => {
  const realCount = filteredStaff.value.filter(e => !e.id.startsWith('fake-')).length
  return selectedEmployees.value.length > 0 && selectedEmployees.value.length < realCount
})

function toggleAllSelection(value: boolean) {
  if (value) {
    const realItems = filteredStaff.value.filter(e => !e.id.startsWith('fake-'))
    selectedEmployees.value = realItems.map(e => e.id)
    selectedEmployeesCache.value.clear()
    realItems.forEach(e => {
      selectedEmployeesCache.value.set(e.id, e)
    })
  } else {
    selectedEmployees.value = []
    selectedEmployeesCache.value.clear()
  }
}

function toggleEmployeeSelection(itemOrId?: IStaffEmployee | string) {
  if (!itemOrId) return
  const id = typeof itemOrId === 'string' ? itemOrId : itemOrId.id
  if (!id) return

  const index = selectedEmployees.value.indexOf(id)
  if (index === -1) {
    selectedEmployees.value.push(id)
    const emp = typeof itemOrId === 'object'
      ? itemOrId
      : filteredStaff.value.find(e => e.id === id) || staffStore.staffList.find(e => e.id === id)
    if (emp) {
      selectedEmployeesCache.value.set(id, emp)
    }
  } else {
    selectedEmployees.value.splice(index, 1)
    selectedEmployeesCache.value.delete(id)
  }
}

// Хелперы ролей
// function getRoleLabel(role?: string): string {
//   if (role === 'admin') return 'Администратор'
//   if (role === 'quality_control') return 'Контроль качества'
//   return 'Пользователь'
// }

// function getRoleColor(role?: string): string {
//   if (role === 'admin') return 'error'
//   if (role === 'quality_control') return 'warning'
//   return 'primary'
// }

// Хелперы статусов
function getStatusLabel(status: string): string {
  if (status === 'active' || status === 'Активен') return 'Активен'
  if (status === 'inactive' || status === 'Не активен') return 'Не активен'
  if (status === 'dismissed' || status === 'Уволен') return 'Уволен'
  return status
}

function getStatusColor(status: TEmployeeStatus): string {
  if (status === 'Не активен') return 'warning'
  if (status === 'Уволен') return 'error'
  return 'success'
}

// Действия диалогов
// function openBlockDialog(emp: IStaffEmployee) {
//   selectedEmployee.value = emp
//   isBulkBlock.value = false
//   isBlockDialogOpen.value = true
// }

function openBulkBlockDialog() {
  isBulkBlock.value = true
  selectedEmployee.value = null
  isBlockDialogOpen.value = true
}

async function confirmBlock() {
  if (isBulkBlock.value) {
    const idsToBlock = [...selectedEmployees.value]
    try {
      await Promise.all(idsToBlock.map(id => staffStore.blockEmployee(id, { reason: '', until: null })))
      selectedEmployees.value = []
      selectedEmployeesCache.value.clear()
    } catch (e) {
      console.error(e)
    }
  } else if (selectedEmployee.value) {
    await staffStore.blockEmployee(selectedEmployee.value.id, { reason: '', until: null })
  }
  isBlockDialogOpen.value = false
}

// function openDismissDialog(emp: IStaffEmployee) {
//   selectedEmployee.value = emp
//   isDismissDialogOpen.value = true
// }

function confirmDismiss() {
  if (selectedEmployee.value) {
    const today = new Date().toISOString().split('T')[0]
    staffStore.dismissEmployee(selectedEmployee.value.id, {
      dismissalDate: today,
      dismissalType: 'own_will',
      reason: '',
    })
  }
  isDismissDialogOpen.value = false
}

function openBulkRestoreDialog() {
  isBulkRestore.value = true
  selectedEmployee.value = null
  isRestoreDialogOpen.value = true
}

function openRestoreDialog(emp: IStaffEmployee) {
  isBulkRestore.value = false
  selectedEmployee.value = emp
  isRestoreDialogOpen.value = true
}

const employeeToRestoreName = computed(() => {
  if (!isBulkRestore.value && selectedEmployee.value) {
    return selectedEmployee.value.full_name || [selectedEmployee.value.last_name, selectedEmployee.value.first_name, selectedEmployee.value.middle_name].filter(Boolean).join(' ')
  }
  if (selectedInactiveEmployees.value.length === 1) {
    const emp = selectedInactiveEmployees.value[0]
    return emp.full_name || [emp.last_name, emp.first_name].filter(Boolean).join(' ')
  }
  return ''
})

function rememberRestoredEmployee(id: string) {
  const fresh = staffStore.staffList.find(employee => employee.id === id)
  const cached = selectedEmployeesCache.value.get(id)
  const next = fresh ?? (cached ? { ...cached, status: 'Активен' } : null)
  if (!next) return

  const map = new Map(selectedEmployeesCache.value)
  map.set(id, next)
  selectedEmployeesCache.value = map
}

async function confirmRestore() {
  if (isBulkRestore.value) {
    const idsToRestore = selectedInactiveEmployees.value.map(emp => emp.id)
    try {
      await Promise.all(idsToRestore.map(id => staffStore.restoreEmployee(id)))
      selectedEmployees.value = selectedEmployees.value.filter(id => !idsToRestore.includes(id))
      idsToRestore.forEach(id => selectedEmployeesCache.value.delete(id))
    } catch (e) {
      console.error(e)
    }
  } else if (selectedEmployee.value) {
    const id = selectedEmployee.value.id
    const restored = await staffStore.restoreEmployee(id)
    if (restored) rememberRestoredEmployee(id)
  }
  isRestoreDialogOpen.value = false
  isBulkRestore.value = false
}

function openEmployeeProfile(emp: IStaffEmployee) {
  selectedEmployeeId.value = emp.id
  isProfileSidebarOpen.value = true
}

function openEmployeeProfileById(employeeId: string) {
  selectedEmployeeId.value = employeeId
  isProfileSidebarOpen.value = true
}

async function openChatWithEmployee(item: IStaffEmployee) {
  if (!item.id) {
    return
  }

  feedbackStore.isGlobalLoading = true
  try {
    await openOrCreateChat(item.id, false, {
      fullName: getFullName(item),
      phone: item.phone_number,
      avatar: item.avatar,
    })
  } finally {
    feedbackStore.isGlobalLoading = false
  }
}

// Групповые действия
const { download: downloadExport, isExporting } = useExportUrl(
  '/api/employee/management/export/',
  () => staffStore.filters,
  () => selectedEmployees.value.length ? { ids: selectedEmployees.value } : {},
)

// Заглушки
function stubAction(title: string) {
  feedbackStore.showToast({
    type: 'info',
    message: `Действие "${title}" будет реализовано в следующих версиях`,
    time: 4000,
  })
}
</script>

<template>
  <!-- Верхняя панель управления -->
  <div
    :class="[
      smAndDown
        ? 'd-flex flex-column gap-2 mb-4 w-100'
        : 'd-flex flex-wrap align-center justify-space-between gap-4 mb-6',
    ]"
  >
    <VBtn
      v-if="permissionsStore.modules.employee_management?.employee_edit"
      color="primary"
      prepend-icon="ri-add-line"
      :class="{ 'w-100': smAndDown }"
      :data-onboarding="STAFF_ANCHORS.table.add"
      @click="isAddEmployeeDialogOpen = true"
    >
      Добавить сотрудника
    </VBtn>
    <div
      class="d-flex gap-3"
      :class="smAndDown ? 'flex-column w-100' : 'align-center'"
    >
      <VBtn
        variant="outlined"
        color="secondary"
        class="bg-surface"
        :class="{ 'w-100': smAndDown }"
        prepend-icon="ri-pie-chart-line"
        :data-onboarding="STAFF_ANCHORS.table.statistics"
        @click="router.push({ name: ERouteNames.STAFF_GRAPH })"
      >
        Статистика
      </VBtn>
      <VBtn
        variant="outlined"
        color="secondary"
        class="bg-surface"
        :class="{ 'w-100': smAndDown }"
        prepend-icon="ri-git-merge-line"
        :data-onboarding="STAFF_ANCHORS.table.structure"
        @click="isStructureModalOpen = true"
      >
        Структура
      </VBtn>
      <VBtn
        v-if="permissionsStore.modules.employee_management?.department_edit || permissionsStore.modules.employee_management?.position_edit"
        variant="outlined"
        color="secondary"
        class="bg-surface"
        :class="{ 'w-100': smAndDown }"
        prepend-icon="ri-settings-4-line"
        :data-onboarding="STAFF_ANCHORS.table.settings"
        @click="router.push({ name: ERouteNames.STAFF_SETTINGS })"
      >
        Настройки
      </VBtn>
      <OnboardingLaunchButton scenario="staff" />

      <!--
      <VBtn
        variant="outlined"
        color="secondary"
        class="bg-surface d-none"
        :class="{ 'w-100': smAndDown }"
        prepend-icon="ri-download-2-line"
        @click="stubAction('Импортировать данные')"
      >
        Импортировать данные
      </VBtn>
      -->
    </div>
  </div>

  <!-- Единая карточка таблицы -->
  <VCard class="table-card border rounded pa-0 overflow-hidden">
    <!-- Поиск -->
    <div
      class="d-flex align-center gap-4"
      :class="[
        tableLogic.activeFilterChips.value.length ? 'px-4 pt-4' : 'pa-4',
        { 'flex-column': smAndDown },
      ]"
    >
      <div
        class="d-flex align-center gap-2 w-100"
        :class="{ 'flex-column': smAndDown }"
      >
        <VTextField
          v-model="tableLogic.searchFilter.value"
          placeholder="Поиск"
          prepend-inner-icon="ri-search-line"
          variant="underlined"
          density="compact"
          hide-details
          clearable
          class="flex-grow-0 search-field search-underlined"
          :class="smAndDown ? 'w-100' : 'w-25'"
          :data-onboarding="STAFF_ANCHORS.table.search"
          @keyup.enter="tableLogic.applySearchFilter()"
        />

        <VTooltip
          v-if="tableLogic.searchFilter.value"
          text="Применить поиск"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <VIconBtn
              v-bind="tooltipProps"
              :class="{ 'w-100': smAndDown }"
              color="primary"
              class="rounded-sm"
              icon="ri-filter-2-line"
              variant="outlined"
              :rounded="false"
              width="36"
              height="36"
              @click="tableLogic.applySearchFilter()"
            />
          </template>
        </VTooltip>

        <VBtn
          v-if="tableLogic.hasActiveFilters.value"
          :class="[
            { 'w-100': smAndDown },
            !smAndDown && 'ml-auto',
          ]"
          prepend-icon="ri-delete-bin-line"
          variant="outlined"
          color="secondary"
          class="text-color-secondary"
          :data-onboarding="STAFF_ANCHORS.table.resetFilters"
          :loading="staffStore.isLoading"
          @click="tableLogic.renewDataAndResetFilters()"
        >
          Сбросить фильтры
        </VBtn>

        <VSwitch
          v-model="isHideInactive"
          :data-onboarding="STAFF_ANCHORS.table.hideInactive"
          label="Скрыть неактивных"
          density="compact"
          color="primary"
          hide-details
          class="flex-grow-0"
          :class="{ 'ml-auto': !smAndDown && !tableLogic.hasActiveFilters.value }"
        />
      </div>
    </div>

    <!-- Активные фильтры -->
    <div
      v-if="tableLogic.activeFilterChips.value.length"
      class="filter-chip d-flex ml-4 align-center gap-2 py-3"
    >
      <VChip
        v-for="chip in tableLogic.activeFilterChips.value"
        :key="chip.key || chip.id || `${chip.config?.key || chip.type}-${chip.value ?? 'no-value'}`"
        closable
        @click:close="tableLogic.removeFilter(chip)"
      >
        {{ chip.label }}
      </VChip>
    </div>

    <!-- Панель групповых действий под поиском -->
    <div
      v-if="permissionsStore.modules.employee_management?.employee_view || permissionsStore.modules.employee_management?.employee_edit || permissionsStore.modules.employee_management?.role_edit"
      class="d-flex border-t border-b px-4 py-2 actions-bar"
      :class="smAndDown ? 'flex-column gap-2 py-3' : 'align-center'"
    >
      <div
        class="d-flex gap-2"
        :class="smAndDown ? 'flex-column w-100' : 'align-center'"
        style="min-width: 0;"
      >
        <VBtn
          v-if="permissionsStore.modules.employee_management?.role_edit"
          :data-onboarding="STAFF_ANCHORS.table.bulkRoles"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-settings-3-line"
          :disabled="selectedEmployees.length === 0"
          :class="smAndDown ? 'w-100' : ''"
          style="min-width: 0; padding-inline: 8px;"
          @click="openBulkRoleSettings"
        >
          <span class="text-truncate">Настройка ролей</span>
        </VBtn>

        <VBtn
          v-if="permissionsStore.modules.employee_management?.employee_edit"
          :data-onboarding="STAFF_ANCHORS.table.bulkBlock"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-lock-line"
          :disabled="selectedEmployees.length === 0"
          :class="smAndDown ? 'w-100' : ''"
          style="min-width: 0; padding-inline: 8px;"
          @click="openBulkBlockDialog"
        >
          <span class="text-truncate">Заблокировать</span>
        </VBtn>

        <VBtn
          v-if="permissionsStore.modules.employee_management?.employee_edit"
          :data-onboarding="STAFF_ANCHORS.table.bulkRestore"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-lock-unlock-line"
          :disabled="selectedInactiveEmployees.length === 0"
          :class="smAndDown ? 'w-100' : ''"
          style="min-width: 0; padding-inline: 8px;"
          @click="openBulkRestoreDialog"
        >
          <span class="text-truncate">Восстановить</span>
        </VBtn>

        <VBtn
          v-if="permissionsStore.modules.employee_management?.employee_edit || permissionsStore.modules.employee_management?.role_edit"
          :data-onboarding="STAFF_ANCHORS.table.bulkExport"
          variant="outlined"
          color="secondary"
          prepend-icon="ri-upload-2-line"
          :class="smAndDown ? 'w-100' : ''"
          style="min-width: 0; padding-inline: 8px;"
          :loading="isExporting"
          @click="downloadExport"
        >
          <span class="text-truncate">Выгрузить</span>
        </VBtn>
        <ReportTemplateMenu
          v-if="permissionsStore.modules.employee_management?.employee_view"
          object-type="employee"
          context="collection"
          variant="toolbar"
          :ids="selectedEmployees"
          :filters="reportFilters"
          :sort="staffStore.currentSortString"
          :selection-label="selectedEmployees.length ? `Выбранные сотрудники: ${selectedEmployees.length}.` : 'Сотрудники по текущим фильтрам, со всех страниц.'"
        />
      </div>
    </div>

    <VDataTableServer
      :key="headers.length"
      v-model:items-per-page="itemsPerPage"
      :headers="headers"
      :items="tableRows"
      :items-length="staffStore.totalItemsCount"
      :row-props="rowProps"
      :loading="staffStore.isLoading"
      loading-text=""
      item-value="id"
      class="custom-data-table"
      :items-per-page-options="[25, 50, 100]"
      @click:row="onRowClick"
      @update:options="tableLogic.handlePageChange"
    >
      <template #no-data>
        <div class="text-center text-italic py-4">
          Данные отсутствуют
        </div>
      </template>

      <!-- Шапка для первой колонки (чекбокс "Выбрать все" или "Меню") -->
      <template #[`header.select_and_menu`]>
        <div class="d-flex align-center gap-2 pl-2">
          <VCheckboxBtn
            v-if="permissionsStore.modules.employee_management?.employee_edit || permissionsStore.modules.employee_management?.role_edit"
            :model-value="isAllSelected"
            :indeterminate="isSomeSelected"
            @update:model-value="toggleAllSelection"
          />
          <div v-if="permissionsStore.modules.employee_management?.employee_edit || permissionsStore.modules.employee_management?.role_edit" style="width: 32px;" /> <!-- Отступ под выравнивание бургера -->
          <span v-if="!permissionsStore.modules.employee_management?.employee_view && !permissionsStore.modules.employee_management?.employee_edit && !permissionsStore.modules.employee_management?.role_edit">Меню</span>
        </div>
      </template>

      <!-- ДИНАМИЧЕСКИЕ ЗАГОЛОВКИ ФИЛЬТРОВ ИЗ COMPOSABLE -->
      <template
        v-for="col in headers.filter((h: any) => h.filterType)"
        :key="col.key"
        #[`header.${col.key}`]="{ column }"
      >
        <div class="header-content d-flex align-center" :class="{ 'justify-center': column.align === 'center' }">
          <span>{{ column.title }}</span>

          <VIcon
            v-if="column.sortable"
            size="16"
            class="ml-1 custom-sort-icon"
            :icon="tableLogic.getSortIcon(col.key)"
            :class="{
              'is-active': tableLogic.isSortActive(col.key),
              'is-desc': tableLogic.isSortDesc(col.key),
            }"
          />

          <!-- Спиннер загрузки фильтров -->
          <div
            v-if="tableLogic.filterLoading?.[col.filterKey!]"
            class="d-flex align-center justify-center ml-1"
            style="width: 28px; height: 28px;"
          >
            <VProgressCircular
              indeterminate
              color="primary"
              size="20"
              width="2"
            />
          </div>

          <VIconBtn
            v-else
            variant="plain"
            size="small"
            class="filter-icon-btn"
            :data-onboarding="STAFF_ANCHORS.table.filterPrefix + col.key"
            :icon="tableLogic.getFilterIcon(col.filterKey!)"
            @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
          />

          <!-- Tree/Universal Filter Menu -->
          <VMenu
            v-if="col.filterType === 'tree'"
            v-model="tableLogic.filterMenus.value[col.filterKey!]"
            :close-on-content-click="false"
            location="bottom start"
            transition="scale-transition"
          >
            <template #activator="{ props: menuProps }">
              <div v-bind="menuProps" />
            </template>
            <UniversalFilterTree
              :field-key="col.filterKey!"
              :store="staffStore"
              :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
              :placeholder="`Поиск: ${column.title}`"
              @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
              @apply="tableLogic.applyFilter(col.filterKey!)"
            />
          </VMenu>

          <!-- Date Range Filter Menu -->
          <VMenu
            v-else-if="col.filterType === 'date_range'"
            v-model="tableLogic.filterMenus.value[col.filterKey!]"
            :close-on-content-click="false"
            location="bottom"
            transition="scale-transition"
            :max-width="220"
          >
            <template #activator="{ props: menuProps }">
              <div v-bind="menuProps" />
            </template>
            <DateRangeFilterCard
              :date-after="tableLogic.tempValues.value.last_login_after || ''"
              :date-before="tableLogic.tempValues.value.last_login_before || ''"
              @update:date-after="tableLogic.tempValues.value.last_login_after = $event"
              @update:date-before="tableLogic.tempValues.value.last_login_before = $event"
              @apply="applyLastLoginDateFilter"
            />
          </VMenu>
        </div>
      </template>

      <!-- ЗАГОЛОВКИ СОРТИРОВКИ ДЛЯ КОЛОНОК БЕЗ ФИЛЬТРОВ -->
      <template
        v-for="col in headers.filter((h: any) => h.sortable && !h.filterType)"
        :key="`sort-${col.key}`"
        #[`header.${col.key}`]="{ column }"
      >
        <div class="header-content d-flex align-center">
          <span>{{ column.title }}</span>
          <VIcon
            size="16"
            class="ml-1 custom-sort-icon"
            :icon="tableLogic.getSortIcon(col.key)"
            :class="{
              'is-active': tableLogic.isSortActive(col.key),
              'is-desc': tableLogic.isSortDesc(col.key),
            }"
          />
        </div>
      </template>

      <!-- Колонка Чекбокс + Бургер-меню действий -->
      <template #item.select_and_menu="{ item }">
        <div class="d-flex align-center gap-2 pl-2">
          <VCheckboxBtn
            v-if="permissionsStore.modules.employee_management?.employee_edit || permissionsStore.modules.employee_management?.role_edit"
            :model-value="item?.id ? selectedEmployees.includes(item.id) : false"
            @update:model-value="toggleEmployeeSelection(item)"
            @click.stop
          />
          <VMenu v-if="item" location="bottom start">
            <template #activator="{ props }">
              <VIconBtn
                v-bind="props"
                size="small"
                variant="text"
                :data-onboarding="item.id === onboardingActivityDemoId ? STAFF_ANCHORS.table.rowMenu : undefined"
                @click.stop
              >
                <VIcon icon="ri-menu-line" size="18" class="opacity-70" />
              </VIconBtn>
            </template>
            <VList density="compact">
              <!-- Профиль -->
              <VListItem prepend-icon="ri-user-line" @click="openEmployeeProfile(item)">
                <VListItemTitle>Профиль</VListItemTitle>
              </VListItem>

              <!-- Восстановить -->
              <VListItem
                v-if="permissionsStore.modules.employee_management?.employee_edit && item.status === 'Не активен'"
                prepend-icon="ri-lock-unlock-line"
                @click="openRestoreDialog(item)"
              >
                <VListItemTitle>Восстановить</VListItemTitle>
              </VListItem>

              <!-- Написать -->
              <VListItem
                prepend-icon="ri-chat-1-line"
                @click="openChatWithEmployee(item)"
              >
                <VListItemTitle>Написать</VListItemTitle>
              </VListItem>

              <!-- Уволить -->
              <!--
                <VListItem
                v-if="item.status !== 'Уволен'"
                prepend-icon="ri-user-unfollow-line"
                @click="openDismissDialog(item)"
                >
                <VListItemTitle>Уволить</VListItemTitle>
                </VListItem>
              -->

              <!-- Посмотреть активность -->
              <VListItem v-if="item.id && permissionsStore.modules.employee_management?.employee_view" prepend-icon="ri-line-chart-line" @click="navigateToActivity(item.id)">
                <VListItemTitle>Посмотреть активность</VListItemTitle>
              </VListItem>

              <!-- Редактировать -->
              <VListItem v-if="permissionsStore.modules.employee_management?.employee_edit" prepend-icon="ri-edit-line" @click="openEditEmployeeDialog(item)">
                <VListItemTitle>Редактировать</VListItemTitle>
              </VListItem>
            </VList>
          </VMenu>
        </div>
      </template>

      <!-- Слот для ФИО -->
      <template #item.name="{ item }">
        <div
          class="d-flex flex-column py-1 name-cell font-weight-medium"
          :class="{ 'cursor-pointer': permissionsStore.modules.employee_management?.employee_view }"
          @click.stop="permissionsStore.modules.employee_management?.employee_view && item?.id && navigateToActivity(item.id)"
        >
          <span class="text-body-1 font-weight-medium">{{ item?.full_name || getFullName(item) }}</span>
        </div>
      </template>

      <!-- Слот для роли -->
      <template #item.role="{ item }">
        <div
          class="d-flex flex-wrap gap-1 align-center"
          :class="{ 'cursor-pointer': permissionsStore.modules.employee_management?.role_edit }"
          style="min-height: 32px; width: 100%;"
          @click.stop="item && permissionsStore.modules.employee_management?.role_edit && openSingleRoleSettings(item)"
        >
          <template
            v-for="role in (item?.roles || [])"
            :key="role.sub"
          >
            <VTooltip
              location="top"
              :disabled="!role.description"
            >
              <template #activator="{ props: tooltipProps }">
                <VChip
                  v-bind="tooltipProps"
                  size="small"
                  class="custom-admin-chip"
                  :class="{ 'cursor-pointer': permissionsStore.modules.employee_management?.role_edit }"
                  variant="tonal"
                >
                  {{ role.name }}
                </VChip>
              </template>
              <span>{{ role.description }}</span>
            </VTooltip>
          </template>
        </div>
      </template>

      <!-- Слот для должности -->
      <template #item.position="{ item }">
        <div class="staff-body-cell" style="max-width: 180px; white-space: normal; word-break: keep-all;">
          {{ item?.position?.name || '' }}
        </div>
      </template>

      <!-- Слот для телефона -->
      <template #item.phone="{ item }">
        <div v-if="(item?.phone_number && item.phone_number !== 'None') || (item?.work_phone_number && item.work_phone_number !== 'None')" class="d-flex flex-column staff-body-cell" style="max-width: 180px;">
          <div v-if="item.phone_number && item.phone_number !== 'None'">
            <span>Личный:</span> {{ item.phone_number }}
          </div>
          <div v-if="item.work_phone_number && item.work_phone_number !== 'None'">
            <span>Рабочий:</span> {{ item.work_phone_number }}
          </div>
        </div>
        <span v-else class="staff-body-cell">телефон не указан</span>
      </template>

      <!-- Слот для последнего входа -->
      <template #item.last_login="{ item }">
        <span class="staff-body-cell">
          {{ item?.last_login ? formatRelativeTime(item.last_login) : 'никогда' }}
        </span>
      </template>

      <!-- Слот для подразделения -->
      <template #item.department="{ item }">
        <VTooltip
          v-if="item?.department?.id"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <div
              v-bind="tooltipProps"
              style="white-space: normal; word-break: keep-all;"
              class="cursor-pointer department-cell staff-body-cell"
              @click.stop="openDepartmentInfo(item.department.id, item.department.name, item)"
            >
              {{ item.department.name || '' }}
            </div>
          </template>
          <span>{{ getDepartmentHierarchy(item.department.id) }}</span>
        </VTooltip>
        <div
          v-else
          style="white-space: normal; word-break: keep-all;"
          class="staff-body-cell"
        >
          —
        </div>
      </template>

      <!-- Слот для статуса -->
      <template #item.status="{ item }">
        <VChip
          v-if="item?.status"
          :color="getStatusColor(item.status)"
          size="small"
          variant="tonal"
        >
          {{ getStatusLabel(item.status) }}
        </VChip>
      </template>

      <!-- Кастомный Footer -->
      <template #bottom>
        <div
          class="custom-data-table-footer"
          :class="{ 'hide-next-button': tableLogic.hideNextButton.value }"
        >
          <VDataTableFooter
            show-current-page
            :items-per-page-options="[25, 50, 100]"
          />
        </div>
      </template>
    </VDataTableServer>
  </VCard>

  <!-- Диалоги и Модалки -->
  <StructureModal
    v-model="isStructureModalOpen"
    :expand-first-on-open="onboardingStore.scenario?.section === 'employee'"
    @select-employee="openEmployeeProfileById"
  />

  <!-- Новые простые модалки подтверждения -->
  <!-- Диалог блокировки -->
  <VDialog
    v-model="isBlockDialogOpen"
    max-width="560"
    persistent
    no-click-animation
    class="confirm-exit-dialog"
  >
    <VCard class="d-flex flex-column bg-surface" style="padding: 20px !important; border-radius: 10px !important; border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important; width: 560px !important; height: auto !important;">
      <div style="padding: 0 0 12px !important; height: auto !important;">
        <h3 style="font-family: 'Inter', sans-serif !important; font-size: 18px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important; line-height: 28px !important; margin: 0;">
          Подтверждение действия
        </h3>
      </div>
      <div style="padding: 0 0 20px !important; height: auto !important;">
        <p style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.7) !important; line-height: 20px !important; margin: 0;">
          <template v-if="isBulkBlock && selectedEmployees.length > 1">
            Вы действительно хотите заблокировать выбранных пользователей
            <VMenu
              open-on-hover
              :close-on-content-click="false"
              close-delay="200"
              location="bottom start"
              offset="8"
            >
              <template #activator="{ props: menuProps }">
                <span
                  v-bind="menuProps"
                  class="text-primary font-weight-medium cursor-pointer d-inline-block"
                  style="text-decoration: underline; text-underline-offset: 3px;"
                >
                  ({{ selectedEmployees.length }} шт.)
                </span>
              </template>

              <VCard class="pa-4 rounded-lg elevation-4 border bg-surface" min-width="280" max-width="360">
                <div class="text-subtitle-2 font-weight-bold text-high-emphasis mb-3">
                  Выбрано {{ selectedEmployees.length }}:
                </div>
                <div class="max-h-300 overflow-y-auto pr-1 d-flex flex-column gap-2">
                  <div
                    v-for="emp in selectedEmployeesList"
                    :key="emp.id"
                    class="d-flex align-center justify-space-between py-2"
                  >
                    <div class="d-flex align-center gap-2">
                      <VAvatar :image="getEmployeeAvatar(emp)" variant="tonal" size="36" />
                      <div class="d-flex flex-column" style="min-width: 0;">
                        <span class="text-body-2 font-weight-medium text-high-emphasis text-truncate" style="max-width: 160px;">
                          {{ emp.full_name || `${emp.last_name} ${emp.first_name}`.trim() || 'Неизвестный сотрудник' }}
                        </span>
                        <span class="text-caption text-medium-emphasis text-wrap">
                          {{ emp.position?.name || 'Должность' }}
                        </span>
                      </div>
                    </div>
                    <VIconBtn
                      size="small"
                      variant="text"
                      color="secondary"
                      class="ms-2 opacity-70 hover-opacity-100"
                      @click="removeEmployeeFromSelection(emp.id)"
                    >
                      <VIcon icon="ri-close-circle-fill" size="18" />
                    </VIconBtn>
                  </div>
                </div>
              </VCard>
            </VMenu>?
          </template>
          <template v-else>
            Вы действительно хотите заблокировать пользователя <strong>{{ employeeToBlockName }}</strong>?
          </template>
        </p>
      </div>
      <div style="padding: 0 !important; height: auto !important;">
        <div class="d-flex justify-end gap-4">
          <VBtn
            variant="outlined"
            class="action-btn confirm-btn-cancel"
            @click="isBlockDialogOpen = false"
          >
            Отменить
          </VBtn>
          <VBtn
            color="primary"
            class="action-btn confirm-btn-action"
            @click="confirmBlock"
          >
            Заблокировать
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>

  <!-- Диалог увольнения -->
  <VDialog
    v-model="isDismissDialogOpen"
    max-width="560"
    persistent
    no-click-animation
    class="confirm-exit-dialog"
  >
    <VCard class="d-flex flex-column bg-surface" style="padding: 20px !important; border-radius: 10px !important; border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important; width: 560px !important; height: auto !important;">
      <div style="padding: 0 0 12px !important; height: auto !important;">
        <h3 style="font-family: 'Inter', sans-serif !important; font-size: 18px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important; line-height: 28px !important; margin: 0;">
          Подтверждение действия
        </h3>
      </div>
      <div style="padding: 0 0 20px !important; height: auto !important;">
        <p style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.7) !important; line-height: 20px !important; margin: 0;">
          Вы действительно хотите уволить пользователя <strong>{{ selectedEmployee?.full_name || '' }}</strong>?
        </p>
      </div>
      <div style="padding: 0 !important; height: auto !important;">
        <div class="d-flex justify-end gap-4">
          <VBtn
            variant="outlined"
            class="action-btn confirm-btn-cancel"
            @click="isDismissDialogOpen = false"
          >
            Отменить
          </VBtn>
          <VBtn
            color="primary"
            class="action-btn confirm-btn-action"
            @click="confirmDismiss"
          >
            Уволить
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>

  <!-- Диалог восстановления -->
  <VDialog
    v-model="isRestoreDialogOpen"
    max-width="560"
    persistent
    no-click-animation
    class="confirm-exit-dialog"
  >
    <VCard class="d-flex flex-column bg-surface" style="padding: 20px !important; border-radius: 10px !important; border: 1px solid rgba(var(--v-theme-on-surface), 0.08) !important; width: 560px !important; height: auto !important;">
      <div style="padding: 0 0 12px !important; height: auto !important;">
        <h3 style="font-family: 'Inter', sans-serif !important; font-size: 18px !important; font-weight: 500 !important; color: rgba(var(--v-theme-on-surface), 0.9) !important; line-height: 28px !important; margin: 0;">
          Подтверждение действия
        </h3>
      </div>
      <div style="padding: 0 0 20px !important; height: auto !important;">
        <p style="font-family: 'Inter', sans-serif !important; font-size: 15px !important; font-weight: 400 !important; color: rgba(var(--v-theme-on-surface), 0.7) !important; line-height: 20px !important; margin: 0;">
          <template v-if="isBulkRestore && selectedInactiveEmployees.length > 1">
            Вы действительно хотите восстановить выбранных пользователей
            <VMenu
              open-on-hover
              :close-on-content-click="false"
              close-delay="200"
              location="bottom start"
              offset="8"
            >
              <template #activator="{ props: menuProps }">
                <span
                  v-bind="menuProps"
                  class="text-primary font-weight-medium cursor-pointer d-inline-block"
                  style="text-decoration: underline; text-underline-offset: 3px;"
                >
                  ({{ selectedInactiveEmployees.length }} шт.)
                </span>
              </template>

              <VCard class="pa-4 rounded-lg elevation-4 border bg-surface" min-width="280" max-width="360">
                <div class="text-subtitle-2 font-weight-bold text-high-emphasis mb-3">
                  Выбрано {{ selectedInactiveEmployees.length }}:
                </div>
                <div class="max-h-300 overflow-y-auto pr-1 d-flex flex-column gap-2">
                  <div
                    v-for="emp in selectedInactiveEmployees"
                    :key="emp.id"
                    class="d-flex align-center justify-space-between py-2"
                  >
                    <div class="d-flex align-center gap-2">
                      <VAvatar :image="getEmployeeAvatar(emp)" variant="tonal" size="36" />
                      <div class="d-flex flex-column" style="min-width: 0;">
                        <span class="text-body-2 font-weight-medium text-high-emphasis text-truncate" style="max-width: 160px;">
                          {{ emp.full_name || `${emp.last_name} ${emp.first_name}`.trim() || 'Неизвестный сотрудник' }}
                        </span>
                        <span class="text-caption text-medium-emphasis text-wrap">
                          {{ emp.position?.name || 'Должность' }}
                        </span>
                      </div>
                    </div>
                    <VIconBtn
                      size="small"
                      variant="text"
                      color="secondary"
                      class="ms-2 opacity-70 hover-opacity-100"
                      @click="removeEmployeeFromSelection(emp.id)"
                    >
                      <VIcon icon="ri-close-circle-fill" size="18" />
                    </VIconBtn>
                  </div>
                </div>
              </VCard>
            </VMenu>?
          </template>
          <template v-else>
            Восстановить пользователя <strong>{{ employeeToRestoreName }}</strong>?
          </template>
        </p>
      </div>
      <div style="padding: 0 !important; height: auto !important;">
        <div class="d-flex justify-end gap-4">
          <VBtn
            variant="outlined"
            class="action-btn confirm-btn-cancel"
            @click="isRestoreDialogOpen = false"
          >
            Отменить
          </VBtn>
          <VBtn
            color="primary"
            class="action-btn confirm-btn-action"
            @click="confirmRestore"
          >
            Восстановить
          </VBtn>
        </div>
      </div>
    </VCard>
  </VDialog>

  <EmployeeProfileSidebar
    v-model="isProfileSidebarOpen"
    :employee-id="selectedEmployeeId"
  />

  <DepartmentInfoSidebar
    v-model="isDepartmentSidebarOpen"
    :department-id="selectedDepartmentId"
    :department-name="selectedDepartmentName"
    :employee="selectedEmployeeForSidebar"
  />

  <AddEmployeeDialog v-model="isAddEmployeeDialogOpen" />

  <RoleSettingsDialog
    v-model="isRoleSettingsDialogOpen"
    v-model:employee-ids="roleSettingsEmployeeIds"
    :employees="Array.from(selectedEmployeesCache.values())"
    @saved="onRoleSettingsSaved"
  />

  <EditEmployeeDialog
    v-model="isEditEmployeeDialogOpen"
    :employee-id="editEmployeeId"
  />
</template>

<style lang="scss" scoped>
.name-cell {
  &:hover {
    text-decoration: underline;
  }
}

.table-card {
  border-color: rgba(var(--v-theme-on-surface), 0.08) !important;
}

.actions-bar {
  border-color: rgba(var(--v-theme-on-surface), 0.08) !important;
}

:deep(.custom-data-table .v-data-table-header__content) {
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity));
  font-size: 15px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 22px;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

.name-cell,
.staff-body-cell {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  line-height: 20px;
}

.name-cell {
  font-weight: 500;
  line-height: 22px;

  .text-body-1 {
    color: inherit !important;
    font-weight: inherit !important;
  }
}

.staff-body-cell {
  font-weight: 400;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  right: 0;
  top: 25%;
  height: 50%;
  width: 1px;
  background-color: rgba(var(--v-theme-on-surface), 0.12);
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
  padding-inline: 1rem !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__items-per-page .v-select__selection-text {
      overflow: visible;
      text-overflow: clip;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled='true'] {
      display: none !important;
    }
  }

  &.hide-next-button {
    :deep(.v-pagination__next) {
      display: none !important;
    }
  }
}

:deep(.search-underlined.v-text-field) {
  .v-field__control {
    height: 36px !important;
  }

  .v-field__prepend-inner {
    align-self: center !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
  }

  .v-field__input {
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    min-height: 36px !important;
    align-self: center !important;
    display: flex !important;
    align-items: center !important;
  }
}

// Стили для кнопок в модальных окнах подтверждения действия

.action-btn {
  font-family: 'Inter', sans-serif !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  height: 38px !important;
  border-radius: 6px !important;
  text-transform: none !important;
  letter-spacing: normal !important;
  box-shadow: none !important;
  padding: 8px 18px !important;
}

.confirm-btn-cancel {
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12) !important;
  color: rgba(var(--v-theme-on-surface), 0.55) !important;
  background-color: transparent !important;
  min-width: 110px !important;

  &:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.04) !important;
  }
}

.confirm-btn-action {
  background-color: rgb(var(--v-theme-primary)) !important;
  color: #ffffff !important;
  min-width: 84px !important;
}

.department-cell {
  transition: color 0.2s ease;

  &:hover {
    color: rgb(var(--v-theme-primary)) !important;
  }
}

:deep(.v-chip.custom-admin-chip) {
  background-color: rgba(var(--v-theme-secondary), 0.16) !important;
  color: rgb(var(--v-theme-secondary)) !important;
}
</style>
